import { Icon, spacing, Text } from '@scality/core-ui';
import { Button } from '@scality/core-ui/dist/next';
import type React from 'react';
import { useMemo } from 'react';
import styled from 'styled-components';
import type { Control, ControlOption } from '../types/controls';

/** Options offered inline; past this the control above is the way through. */
const MAX_INLINE_OPTIONS = 5;

const Ask = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.r12};
  padding: ${spacing.r32} ${spacing.r16};
  text-align: center;
`;

const Body = styled(Text)`
  max-width: 46ch;
`;

const Options = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${spacing.r8};
  margin-top: ${spacing.r4};
`;

type DashboardAskProps = {
  /** The control holding the dashboard back. */
  control: Control;
  /** Already labelled: this component resolves no label of its own. */
  options: ControlOption[];
  optionsLoading: boolean;
  /** What picking gets you, when it is not the dashboard itself. */
  pickHint?: string;
  onPick: (value: string) => void;
};

/** Shown instead of charts while a required criterion has no value, as a question with answers. */
export const DashboardAsk: React.FC<DashboardAskProps> = ({
  control,
  options,
  optionsLoading,
  pickHint,
  onPick,
}) => {
  const multiple = control.multiple ?? false;
  const noun = control.label.toLowerCase();
  const nouns = `${noun}s`;

  const offered = useMemo(() => options.slice(0, MAX_INLINE_OPTIONS), [options]);
  const withheld = options.length - offered.length;

  const question = multiple ? `Which ${nouns}?` : `Which ${noun}?`;
  const nothingToOffer = `Choose a ${noun} in the ${control.label} control above to load this dashboard.`;
  const count = `${options.length} ${options.length === 1 ? noun : nouns} available.`;
  const invitation =
    pickHint ?? (multiple ? ' Pick one to start — you can add more once it loads.' : ' Pick one to load the metrics.');

  const body = optionsLoading ? `Looking up the available ${nouns}…` : options.length === 0 ? nothingToOffer : count + invitation;

  return (
    <Ask>
      <Icon name="Search" size="lg" color="textSecondary" />
      <Text variant="Larger" color="textPrimary">
        {question}
      </Text>
      <Body variant="Basic" color="textSecondary">
        {body}
      </Body>
      {offered.length > 0 && (
        <Options>
          {offered.map((option) => (
            <Button key={option.value} variant="outline" label={option.label} onClick={() => onPick(option.value)} />
          ))}
        </Options>
      )}
      {withheld > 0 && (
        <Text variant="Smaller" color="textSecondary">
          {`Showing ${offered.length} of ${options.length} — use the ${noun} control above to search.`}
        </Text>
      )}
    </Ask>
  );
};
